import { END_POINTS } from '@config';
import ApiTags from '@constants/api-tags';
import { api } from '@core/baseApi';
import { HTTPResponse } from '@types';

import {
  CreateSubscriptionPaymentOrderRequest,
  SubscriptionLedgerItem,
  UpdateOrgDocStatusBody
} from '../types';

const extendedBaseQuery = api.injectEndpoints({
  endpoints: (builder) => ({
    getOrganizationDocuments: builder.query<
      HTTPResponse<any>,
      { organizationId: string }
    >({
      query: (reqBody) => ({
        url: END_POINTS.ADMIN.GET_ORGANIZATION_DOCUMENTS(
          reqBody.organizationId
        ),
        method: 'GET'
      })
    }),
    getAllOrganizations: builder.query<
      HTTPResponse<any>,
      { queryString: string }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.ADMIN.GET_ORGANIZATION_LIST}${reqBody.queryString}`,
        method: 'GET'
      })
    }),
    getAdminRolesAndPermissions: builder.query<HTTPResponse<any>, null>({
      query: () => ({
        url: END_POINTS.ADMIN.GET_ROLES_AND_PERMISSIONS,
        method: 'GET'
      })
    }),
    updateOrganizationStatus: builder.mutation<
      HTTPResponse<any>,
      {
        organizationId: string;
        body: { status: string; reason?: string; remark?: string };
      }
    >({
      query: (reqBody) => ({
        url: END_POINTS.ADMIN.UPDATE_ORGANIZATION_STATUS(
          reqBody.organizationId
        ),
        method: 'POST',
        body: reqBody.body
      })
    }),
    getOrganizationById: builder.query<
      HTTPResponse<any>,
      { organizationId: string }
    >({
      query: (reqBody) => ({
        url: END_POINTS.ADMIN.GET_ORGANIZATION_BY_ID(reqBody.organizationId),
        method: 'GET'
      })
    }),
    updateOrgDocStatus: builder.mutation<
      HTTPResponse<any>,
      { docId: string; body: UpdateOrgDocStatusBody }
    >({
      query: (req) => ({
        url: END_POINTS.ADMIN.UPDATE_ORG_DOC_STATUS(req.docId),
        method: 'PATCH',
        body: req.body
      })
    }),
    getOrganizationCustomerList: builder.query<
      HTTPResponse<any>,
      { queryString: string }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.ADMIN.GET_ORGANIZATION_CUSTOMER_LIST}${reqBody.queryString}`,
        method: 'GET'
      })
    }),
    updateOrganizationCustomerSettings: builder.mutation<
      HTTPResponse<any>,
      {
        organizationId: string;
        body: any;
      }
    >({
      query: (reqBody) => ({
        method: 'POST',
        body: reqBody.body,
        url: END_POINTS.ADMIN.UPDATE_ORGANIZATION_CUSTOMER_SETTINGS(
          reqBody.organizationId
        )
      })
    }),
    updateOrganizationDetails: builder.mutation<
      HTTPResponse<any>,
      {
        body: any;
        actorId: string;
      }
    >({
      query: (reqBody) => ({
        method: 'POST',
        body: reqBody.body,
        headers: {
          'X-Actor-ID': reqBody.actorId
        },
        url: END_POINTS.ADMIN.EXECUTE_COMMAND
      })
    }),
    getBusinessCategories: builder.query<HTTPResponse<any>, null>({
      query: () => ({
        url: END_POINTS.ADMIN.GET_BUSINESS_CATEGORIES,
        method: 'GET'
      })
    }),
    getLatestSignedDocument: builder.query<
      HTTPResponse<any>,
      { organizationId: string; documentType: string }
    >({
      query: (reqBody) => ({
        url: END_POINTS.ADMIN.GET_LATEST_SIGNED_DOCUMENT(
          reqBody.organizationId
        ),
        method: 'GET',
        params: {
          document_type: reqBody.documentType
        }
      })
    }),
    getAdminPlans: builder.query<
      HTTPResponse<{ id: number; name: string }[]>,
      null
    >({
      query: () => ({
        url: END_POINTS.ADMIN.GET_PLANS,
        method: 'GET'
      })
    }),
    createSubscriptionPaymentOrder: builder.mutation<
      HTTPResponse<any>,
      CreateSubscriptionPaymentOrderRequest
    >({
      query: (req) => ({
        method: 'POST',
        headers: {
          'X-Actor-ID': req.actorId
        },
        url: END_POINTS.ADMIN.EXECUTE_COMMAND,
        body: {
          command: 'CREATE_SUBSCRIPTION_PAYMENT_ORDER',
          command_data: {
            organisation_id: req.organisationId,
            collected_from_year: req.collectedFromYear,
            collected_from_month: req.collectedFromMonth,
            collected_till_year: req.collectedTillYear,
            collected_till_month: req.collectedTillMonth,
            amount: req.amount
          },
          note: req.note
        }
      }),
      invalidatesTags: [ApiTags.SUBSCRIPTION_LEDGER]
    }),
    getSubscriptionLedger: builder.query<
      HTTPResponse<SubscriptionLedgerItem[]>,
      { organizationId: string }
    >({
      query: (req) => ({
        url: END_POINTS.ADMIN.GET_SUBSCRIPTION_LEDGER(req.organizationId),
        method: 'GET'
      }),
      providesTags: [ApiTags.SUBSCRIPTION_LEDGER]
    })
  })
});

// eslint-disable-next-line import/prefer-default-export
export const {
  useGetOrganizationDocumentsQuery,
  useLazyGetAllOrganizationsQuery,
  useLazyGetAdminRolesAndPermissionsQuery,
  useUpdateOrganizationStatusMutation,
  useGetOrganizationByIdQuery,
  useLazyGetOrganizationByIdQuery,
  useUpdateOrgDocStatusMutation,
  useLazyGetOrganizationCustomerListQuery,
  useUpdateOrganizationCustomerSettingsMutation,
  useUpdateOrganizationDetailsMutation,
  useGetBusinessCategoriesQuery,
  useGetLatestSignedDocumentQuery,
  useGetAdminPlansQuery,
  useCreateSubscriptionPaymentOrderMutation,
  useGetSubscriptionLedgerQuery
} = extendedBaseQuery;
